// @flow
import React from 'react';
import injectSheet from 'react-jss';
import {
  FLEXIBLE_COLUMNS_VARIANT_1,
  FLEXIBLE_COLUMNS_VARIANT_2,
  FLEXIBLE_COLUMNS_VARIANT_3,
  FLEXIBLE_COLUMNS_VARIANT_4
} from 'shared/constants';
import Dropdown from './Dropdown';
import Checkbox from './Checkbox';
import { S, A, DARK } from '~/app/ui/tokens';

type Option = {
  key: string,
  label: string,
  tooltip?: string,
  type: string,
  options?: string[] | number[]
}

type Props = {
  classes: Object,
  value: {
    count: number,
    variant: string,
    direction: string,
    mobile_keep_direction: boolean,
    remove_column_space: boolean,
    position_column: string,
    position_row: string,
  },
  schema: {
    key: string,
    label: string,
    type: string,
    children: Option[],
  },
  options: string[],
  onChange: Function,
  error: string
}

const variants = {
  1: FLEXIBLE_COLUMNS_VARIANT_1,
  2: FLEXIBLE_COLUMNS_VARIANT_2,
  3: FLEXIBLE_COLUMNS_VARIANT_3,
  4: FLEXIBLE_COLUMNS_VARIANT_4
};

const allowedRowNumber = 4;

const FlexibleColumnLayout = (props: Props) => {
  const { classes: c, value, schema, onChange } = props;
  const draft = { ...value };

  const handleCountChange = (newValue) => {
    const data = {
      ...draft,
      count: newValue,
      variant: newValue < allowedRowNumber ? variants[`${newValue || 1}`][0] : null
    };

    onChange(data);
  };

  const handleDirectionChange = (newValue) => {
    let { count } = value;

    // when switch from column to row, fall back to default when count is greater
    // than number allowed in single row
    if (newValue === 'Single Row' && count > allowedRowNumber) {
      count = 2;
    }

    const data = {
      ...draft,
      direction: newValue,
      count,
    };

    onChange(data);
  };

  const handleColumnPositionChange = (newValue) => {
    const { count, direction } = value;
    let data = {
      ...draft,
      count,
    };

    // Change the layout position based on the direction
    if (direction === 'Single Column') {
      data.position_column = newValue;
    } else {
      data.position_row = newValue;
    }

    onChange(data);
  };

  return (
    <div className={c.Container2}>
      {
        schema.children.map((option) => {
          let component;

          // Options with more complex dependencies need to be configured manually
          // Generic standalone options will work automatically
          if (option.key === 'direction') {
            component = (
              <Dropdown
                value={value[option.key]}
                options={option.options}
                onChange={handleDirectionChange}
              />
            )
          } else if (['position_column', 'position_row'].includes(option.key)) {
            // Renders column or row based on the direction setting
            if (value.direction === 'Single Column' && option.key === 'position_row') return;
            if (value.direction !== 'Single Column' && option.key === 'position_column') return;
            component = (
              <Dropdown
                disabled={!value?.remove_column_space}
                value={value[option.key]}
                options={option.options}
                onChange={handleColumnPositionChange}
              />
            )
          } else if (option.key === 'count') {
            component = (
              <Dropdown
                value={value[option.key]}
                options={value.direction === 'Single Column' ? option.options : option.options.slice(0, allowedRowNumber)}
                onChange={handleCountChange}
              />
            )
          } else if (option.key === 'variant') {
            if (value.direction === 'Single Column') return;
            component = (
              <Dropdown
                value={value[option.key]}
                options={variants[`${value.count || 1}`]}
                onChange={newVal => onChange({ ...draft, [option.key]: newVal })}
              />
            )
          }

          // Generic option types
          if (option.type === 'BOOLEAN') {
            component = (
              <Checkbox
                value={!!value[option.key]}
                onChange={newVal => onChange({ ...draft, [option.key]: newVal })}
              />
            )
          } else if (option.type === 'ENUM') {
            component = (
              <Dropdown
                value={value[option.key]}
                options={option.options}
                onChange={newVal => onChange({ ...draft, [option.key]: newVal })}
              />
            )
          } else {
            // UI error message - generic option type needs to be added
            return (
              <div className={c.invalidTypeError}>
                {`Support for type [${option.type}] is not available in FlexibleColumnLayout`}
              </div>
            )
          }

          // Wrapper + component
          return (
            <div key={option.key} className={c.itemContainer}>
              {option.label}
              {component}
            </div>
          )
        })
      }
    </div>
  )
}

const styles = {
  Container2: {
    boxSizing: 'border-box',
    border: '1px solid hsl(0, 0%, 90%)',
    padding: '10px 10px',
    margin: '20px',
    borderRadius: '5px',
    [DARK]: { borderColor: S.line }
  },
  invalidTypeError: {
    padding: '40px 0',
    color: 'red',
    [DARK]: { color: S.red }
  },
  container: {
    marginTop: '20px'
  },
  itemContainer: {
    marginTop: '20px'
  },
  control: {
    display: 'inline-block',
    border: 'none',
    borderRadius: '3px',
    fontSize: '13px',
    letterSpacing: '0',
    fontFamily: '"Montserrat", sans-serif',
    color: 'hsl(0, 0%, 20%)',
    fontWeight: '400',
    height: '38px',
    width: '120px',
    transition: 'background-color .25s',
    cursor: 'pointer',
    position: 'relative',
    padding: '0 10px',
    backgroundColor: 'hsl(0, 0%, 98%)',
    '&:hover': {
      backgroundColor: 'hsl(0, 0%, 95%)',
      boxShadow: '0px 3px 8px 1px hsl(0, 0%, 80%)'
    },
    [DARK]: { color: S.fg, backgroundColor: S.field, '&:hover': { backgroundColor: S.chromeHover, boxShadow: A.pop } }
  },
  errorMsg: {
    color: 'hsl(360, 100%, 40%)',
    [DARK]: { color: S.red },
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    marginTop: '5px',
    '&> .material-icons': {
      fontSize: '18px',
      marginRight: '5px'
    }
  }
};

export default injectSheet(styles)(FlexibleColumnLayout);
